/** @format */

import {
	GetRecommendationsServiceOptions,
	IElasticProductDetails,
	IRecommendationsRequestBody,
	IRecommendationsRequestProductDetail,
	IRecommendationsResponse
} from '../interfaces/recommendations.interface';
import { Observable, of } from 'rxjs';

import { Injectable } from '@angular/core';
import { ProductRecommendationsDao } from '../dao/product.recommendations.dao';
import { map } from 'rxjs/operators';

/** @dynamic */
@Injectable({
	providedIn: 'root'
})
export class ProductRecommendationsService {
	constructor(private recommendationsDao: ProductRecommendationsDao) {}

	/**
	 * @description
	 * Accepts several parameters and uses them to construct an object used as the request body for getting recommended products. If
	 * the eCode list is not valid or empty, an empty array will be returned.
	 *
	 * @param eCodeList -- The list of eCodes to provide to ds-recs-details to find recommendations for
	 * @param placementId -- The placement ID string of the type of recommendations to get back (e.g. others-you-may-like)
	 * @param filter -- The filter to apply for getting recommendations (e.g. filterOutOfStockItems)
	 * @param priceRerankLevel -- The price re-rank level string (e.g. no-price-reranking)
	 * @param diversityLevel -- The diversity level string (e.g. no-diversity)
	 * @param eventType -- The event type string (e.g. shopping-cart-page-view)
	 * @param athleteId -- The athlete ID string
	 * @param maxNumberOfProductsToReturn -- The maximum number of recommended products to return
	 * @param project
	 * @param purchaseTransaction
	 * @param pageType
	 * @param containerID
	 * @param dsgRecs
	 */
	public getRecommendationsWithProductDetails$({
		eCodeList,
		placementId,
		filter,
		priceRerankLevel,
		diversityLevel,
		eventType,
		athleteId,
		maxNumberOfProductsToReturn,
		project,
		purchaseTransaction,
		pageType,
		containerID,
		dsgRecs
	}: GetRecommendationsServiceOptions): Observable<IElasticProductDetails[]> {
		// Ensure there are eCodes to get recommendations for
		if (!Array.isArray(eCodeList) || (Array.isArray(eCodeList) && eCodeList.length === 0)) {
			// Return an empty array
			return of([]);
		}

		// Construct the list of product details for the request
		const productDetails: IRecommendationsRequestProductDetail[] = eCodeList.map(
			(eCode: string) =>
				({
					product: {
						id: eCode
					},
					quantity: 1
				}) as IRecommendationsRequestProductDetail
		);

		// Construct recommendations request body
		let requestBody: IRecommendationsRequestBody = {
			validateOnly: false,
			params: {},
			userEvent: {
				visitorId: athleteId,
				eventType,
				productDetails
			}
		};

		// Add purchase transaction to request body if event type is "purchase-complete" - BOTF-1739
		if (purchaseTransaction?.id) {
			requestBody.userEvent.purchaseTransaction = purchaseTransaction;
		}
		// Check to see if the filter param has been provided
		if (typeof filter === 'string' && filter.trim() !== '') {
			requestBody = { filter, ...requestBody };
		}

		// Check to see if the diversity level param has been provided
		if (typeof diversityLevel === 'string' && diversityLevel.trim() !== '') {
			requestBody.params = { diversityLevel, ...requestBody.params };
		}

		// Check to see if the price re-rank param has been provided
		if (typeof priceRerankLevel === 'string' && priceRerankLevel.trim() !== '') {
			requestBody.params = { priceRerankLevel, ...requestBody.params };
		}

		// Make DAO method call
		return this.recommendationsDao
			.getRecommendationsWithProductDetails$({
				requestBody,
				placementId,
				maxNumberOfProductsToReturn,
				project,
				returnAllProductDetails: false,
				returnOutOfStockProducts: false,
				pageType,
				containerID,
				dsgRecs
			})
			.pipe(
				map(
					(response: IRecommendationsResponse) =>
						// Return the recommended products results
						response.results
				)
			);
	}
}
